import { MemberState } from '../../../types/zinantis/members/membersType';
import endpoints from '../../../utils/commonVariables/endpoints';
import {
    readOnlyEntities,
    viewModelEntities,
} from '../../../utils/commonVariables/entityKeys';
import { buildOdataEndPoint } from '../../../utils/odataEndPoint';
import { viewModelHotelOwnerZinantisData } from '../../../utils/viewModelStructures/hotelOwnerZinantisData';
import {
    CANCELLED_AS_DISCONNECTED_STATUS_KEY,
    FILTERED_ACCOUNT_MANAGEMENT_SEGMENT_KEY,
    REJECTED_AS_DISCONNECTED_STATUS_KEY,
} from '../../../utils/zinantis/members/membersUtils';
import api from '../../api';
import {
    adaptAccountManagementSegmentsToState,
    adaptMembersToState,
    adaptZinantisStatusToState,
} from './zinantisMemberListAdapters';

export const getAccountManagementSegments = async () => {
    const enpointOptions = {
        entity: readOnlyEntities.AccountManagementSegment,
        filter: {
            and: [
                'Enabled',
                'ForAgency',
                `Key ne '${FILTERED_ACCOUNT_MANAGEMENT_SEGMENT_KEY}'`,
            ],
        },
        orderBy: 'Order asc',
        select: ['Id', 'Key', 'Name', 'LocalizedCaption', 'Caption'],
    };

    const endpoint = buildOdataEndPoint(enpointOptions);
    const { data } = await api.get(endpoint);
    return adaptAccountManagementSegmentsToState(data?.value);
};

export const getZinantisMembers = async (): Promise<MemberState[]> => {
    const endpointOptions = {
        entity: readOnlyEntities.Agency,
        expand: [
            readOnlyEntities.AccountManagementSegment,
            readOnlyEntities.Country,
            'LogoFile',
        ],
        orderBy: 'Name asc',
        select: [
            'Id',
            'Name',
            'Address',
            'IdCountry',
            'Country/LocalizedCaption',
            'Country/Caption',
            'Country/Name',
            'Country/ISO2',
            'LogoFile/VirtualPath',
            'IdAccountManagementSegment',
            'AccountManagementSegment/LocalizedCaption',
            'AccountManagementSegment/Caption',
            'AccountManagementSegment/Name',
            'AccountManagementSegment/Key',
            'ZinantisMemberDate',
        ],
        filter: 'ZinantisMembership eq true',
    };

    const endpoint = buildOdataEndPoint(endpointOptions);
    const { data } = await api.get(endpoint);
    return adaptMembersToState(data.value);
};

export const getZinantisStatuses = async () => {
    const endpointOptions = {
        entity: readOnlyEntities.ZinantisRelationshipStatusType,
        orderBy: 'Name asc',
        select: ['Id', 'Key', 'LocalizedCaption', 'Caption', 'Name', 'Color'],
        filter: {
            and: [
                'Enabled',
                `Key ne '${CANCELLED_AS_DISCONNECTED_STATUS_KEY}'`,
                `Key ne '${REJECTED_AS_DISCONNECTED_STATUS_KEY}'`,
            ],
        },
    };

    const endpoint = buildOdataEndPoint(endpointOptions);
    const { data } = await api.get(endpoint);

    return adaptZinantisStatusToState(data.value);
};

export const getAuxiliaryZinantisMemberData = async () => {
    const [segments, members, statuses] = await Promise.all([
        getAccountManagementSegments(),
        getZinantisMembers(),
        getZinantisStatuses(),
    ]);

    return { segments, members, statuses };
};

export const getHotelOwnerZinantisData = async (idHotelOwner: string) => {
    const endpoint = endpoints.viewModel(
        viewModelEntities.HotelOwner,
        idHotelOwner
    );
    return api.post(endpoint, viewModelHotelOwnerZinantisData);
};

export const requestConnectionToMember = async (idAgency: string) => {
    const requestPayload = { idAgency };
    const endpoint = endpoints.zinantisConnectionRequest;

    return api.post(endpoint, requestPayload);
};

export const cancelRequestToMember = async (idRequest: number | string) => {
    const endpoint = endpoints.zinantisCancelConnectionRequest(idRequest);

    return api.get(endpoint);
};

export const acceptConnectionToMember = async (idRequest: string | number) => {
    const endpoint = endpoints.zinantisAcceptConnectionRequest(idRequest);

    return api.get(endpoint);
};

export const rejectConnectionToMember = async (idRequest: string | number) => {
    const endpoint = endpoints.zinantisRejectConnectionRequest(idRequest);

    return api.get(endpoint);
};
